import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { blogArticles, doctorInfo } from '../mockData';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  ArrowLeft, Calendar, Clock, Phone, Share2, Facebook, Twitter, Linkedin
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';

const BlogDetail = () => {
  const { blogId } = useParams();
  const navigate = useNavigate();
  const article = blogArticles.find(a => a.id === parseInt(blogId));

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-slate-900">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4 text-gray-900 dark:text-slate-50">Article not found</h1>
          <Button onClick={() => navigate('/blog')}>View All Articles</Button>
        </div>
      </div>
    );
  }

  const shareUrl = window.location.href;

  return (
    <>
      <Header />
      <main className="pt-24 pb-16 bg-white dark:bg-slate-900">
        <section className="bg-gradient-to-r from-blue-600 to-blue-700 py-16">
          <div className="max-w-4xl mx-auto px-6">
            <Link to="/blog" className="inline-flex items-center text-white/80 hover:text-white mb-6">
              <ArrowLeft className="w-4 h-4 mr-2" />Back to All Articles
            </Link>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">{article.title}</h1>
            <div className="flex items-center gap-6 text-blue-100">
              <span className="flex items-center gap-2"><Calendar className="w-4 h-4" />{article.date}</span>
              <span className="flex items-center gap-2"><Clock className="w-4 h-4" />{article.readTime}</span>
            </div>
          </div>
        </section>

        <div className="max-w-4xl mx-auto px-6 py-12">
          <div className="grid lg:grid-cols-4 gap-8">
            <div className="lg:col-span-3">
              <div className="rounded-xl overflow-hidden mb-8">
                <img src={article.image} alt={article.title} className="w-full h-64 object-cover" />
              </div>
              <div className="prose prose-lg dark:prose-invert max-w-none">
                <p className="text-xl text-gray-600 dark:text-slate-300 mb-6">{article.excerpt}</p>
                <div className="whitespace-pre-line text-gray-700 dark:text-slate-300 leading-relaxed">{article.content}</div>
              </div>
              <div className="mt-8 pt-8 border-t dark:border-slate-700">
                <div className="flex items-center gap-4">
                  <span className="text-gray-600 dark:text-slate-400 font-medium flex items-center gap-2">
                    <Share2 className="w-4 h-4" />Share:
                  </span>
                  <a href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700">
                    <Facebook className="w-5 h-5" />
                  </a>
                  <a href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${article.title}`} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-sky-500 text-white rounded-full flex items-center justify-center hover:bg-sky-600">
                    <Twitter className="w-5 h-5" />
                  </a>
                  <a href={`https://www.linkedin.com/shareArticle?mini=true&url=${shareUrl}&title=${article.title}`} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-blue-700 text-white rounded-full flex items-center justify-center hover:bg-blue-800">
                    <Linkedin className="w-5 h-5" />
                  </a>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-900/10 border-blue-200 dark:border-blue-800">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-slate-50 mb-4">Need Expert Advice?</h3>
                  <p className="text-gray-600 dark:text-slate-300 mb-4 text-sm">Book a consultation with Dr. Akshay Kawedia for personalized care.</p>
                  <div className="space-y-2">
                    <Link to="/#contact">
                      <Button className="w-full bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-400 text-white" size="sm">Book Appointment</Button>
                    </Link>
                    <a href={`tel:${doctorInfo.phone}`}>
                      <Button variant="outline" className="w-full" size="sm"><Phone className="w-4 h-4 mr-2" />Call Now</Button>
                    </a>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-slate-50 mb-4">More Articles</h3>
                  <ul className="space-y-3">
                    {blogArticles.filter(a => a.id !== parseInt(blogId)).slice(0, 3).map(a => (
                      <li key={a.id}>
                        <Link to={`/blog/${a.id}`} className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline text-sm">{a.title}</Link>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
      <Footer />
      <WhatsAppButton />
    </>
  );
};

export default BlogDetail;
